import { Controller } from "@hotwired/stimulus"
import { animate } from "animejs"

// Метка безопасности Oracle в заголовке.
// Фазы анимации:
//   1. Spinner крутится ПОКА идёт fetch (реальный loading state)
//   2. После ответа: пилюля раскрывается горизонтально
//   3. Текст появляется с fade + translateX
// Состояния: ok | no_mark | no_connection | fetch_error

export default class extends Controller {
  static targets = ["pill", "spinner", "arc", "text", "name", "sl", "abbr",
    "errorMsg", "dropdown"]

  connect() {
    this._reset()

    // Ждём сигнала от виджета на странице
    this._activateHandler = () => this._activate()
    this._beforeCacheHandler = () => this._reset()
    this._beforeRenderHandler = () => this._reset()

    document.addEventListener("security-mark:activate", this._activateHandler, { once: true })
    document.addEventListener("turbo:before-cache", this._beforeCacheHandler)
    document.addEventListener("turbo:before-render", this._beforeRenderHandler)
  }

  disconnect() {
    this._spinAnim?.pause()
    document.removeEventListener("security-mark:activate", this._activateHandler)
    document.removeEventListener("turbo:before-cache", this._beforeCacheHandler)
    document.removeEventListener("turbo:before-render", this._beforeRenderHandler)
  }

  // Сброс в начальное скрытое состояние (вызывается при connect и перед Turbo-кэшем)
  _reset() {
    this._spinAnim?.pause()
    this.pillTarget.style.opacity = "0"
    this.pillTarget.style.maxWidth = "44px"
    this.pillTarget.style.borderColor = ""
    this.spinnerTarget.style.opacity = "1"
    this.spinnerTarget.style.transform = ""
    this.textTarget.style.opacity = "0"
    this.textTarget.style.transform = "translateX(-8px)"
    this.textTarget.style.display = ""
    this.errorMsgTarget.style.display = "none"
    this.errorMsgTarget.style.opacity = "0"
  }

  // Вызывается виджетом через CustomEvent — запускает полную анимацию с нуля
  _activate() {
    // Плавно появляется pill (только иконка) — пользователь видит начало анимации
    animate(this.pillTarget, {
      opacity: [0, 1],
      duration: 350,
      easing: "easeOutQuad",
      onComplete: () => {
        this._startSpinner()
        this._loadMark()
      },
    })
  }

  // ─── Spinner ──────────────────────────────────────────────────────────────

  _startSpinner() {
    const arc = this.arcTarget
    const circ = 2 * Math.PI * 10   // r=10

    arc.style.strokeDasharray = `${circ}`
    arc.style.strokeDashoffset = `${circ}`

    // Рисуем дугу ~75%
    animate(arc, {
      strokeDashoffset: [circ, circ * 0.25],
      duration: 600,
      easing: "easeOutQuad",
    })

    // Крутим бесконечно пока идёт загрузка
    this._spinAnim = animate(this.spinnerTarget, {
      rotate: "1turn",
      duration: 1100,
      easing: "linear",
      loop: true,
    })
  }

  _stopSpinner() {
    this._spinAnim?.pause()
    animate(this.spinnerTarget, {
      opacity: [1, 0],
      duration: 250,
      easing: "easeOutQuad",
    })
  }

  // ─── Data Loading ─────────────────────────────────────────────────────────

  async _loadMark() {
    // Минимум 2с на спиннер — даже если данные пришли мгновенно из кэша
    const minDelay = new Promise(resolve => setTimeout(resolve, 500))

    try {
      const [res] = await Promise.all([
        fetch("/p220/oracle_security_mark", {
          headers: { "Accept": "application/json", "X-Requested-With": "XMLHttpRequest" }
        }),
        minDelay
      ])
      const data = await res.json()

      this._stopSpinner()

      // Небольшая пауза после остановки спиннера — перед раскрытием
      await new Promise(resolve => setTimeout(resolve, 200))

      if (data.status === "ok") {
        this._fillText(data)
        this._expandOk()
      } else {
        this._showError(data.message || "Ошибка метки")
        this._expandError()
      }
    } catch {
      await minDelay
      this._stopSpinner()
      await new Promise(resolve => setTimeout(resolve, 200))
      this._showError("Нет ответа от сервера")
      this._expandError()
    }
  }

  // ─── UI Fill ──────────────────────────────────────────────────────────────

  _fillText(data) {
    this.nameTarget.textContent = data.name || ""
    this.slTarget.textContent = data.sl || "?"
    this.abbrTarget.textContent = data.legal_abbr || "—"
  }

  _showError(msg) {
    // Текст ошибки — в span внутри errorMsg (иконка + span)
    const span = this.errorMsgTarget.querySelector("span")
    if (span) span.textContent = msg
    else this.errorMsgTarget.textContent = msg

    this.textTarget.style.display = "none"
    this.errorMsgTarget.style.display = ""
  }

  // ─── Expand Animations ────────────────────────────────────────────────────

  _expandOk() {
    animate(this.pillTarget, {
      maxWidth: [44, 540],
      duration: 1100,
      easing: "easeOutExpo",
      onComplete: () => {
        animate(this.textTarget, {
          opacity: [0, 1],
          translateX: [-8, 0],
          duration: 900,
          easing: "easeOutQuad",
        })
      },
    })
  }

  _expandError() {
    // Pill меньше + красноватый оттенок через border
    this.pillTarget.style.borderColor = "rgba(239,68,68,0.5)"
    animate(this.pillTarget, {
      maxWidth: [44, 380],
      duration: 700,
      easing: "easeOutExpo",
      onComplete: () => {
        animate(this.errorMsgTarget, {
          opacity: [0, 1],
          translateX: [-8, 0],
          duration: 500,
          easing: "easeOutQuad",
        })
      },
    })
  }

  // ─── Dropdown (Task 2) ────────────────────────────────────────────────────

  async toggleDropdown(event) {
    event.stopPropagation()
    const dd = this.dropdownTarget

    if (!dd.classList.contains("hidden")) {
      this._closeDropdown()
      return
    }

    // Загружаем опции если ещё не загружены
    if (!dd.dataset.loaded) {
      dd.innerHTML = `<div class="px-4 py-3 text-xs text-white/50">Загрузка...</div>`
      dd.classList.remove("hidden")
      await this._loadOptions(dd)
    } else {
      dd.classList.remove("hidden")
    }

    // Анимируем появление
    animate(dd, {
      opacity: [0, 1],
      translateY: [-6, 0],
      duration: 250,
      easing: "easeOutQuad",
    })

    // Закрытие по клику снаружи
    this._outsideHandler = (e) => {
      if (!this.element.contains(e.target)) this._closeDropdown()
    }
    window.addEventListener("click", this._outsideHandler, { once: true })
  }

  _closeDropdown() {
    const dd = this.dropdownTarget
    animate(dd, {
      opacity: [1, 0],
      translateY: [0, -4],
      duration: 180,
      easing: "easeInQuad",
      onComplete: () => dd.classList.add("hidden"),
    })
    window.removeEventListener("click", this._outsideHandler)
  }

  async _loadOptions(dd) {
    try {
      const res = await fetch("/p220/oracle_security_mark/options", {
        headers: { "Accept": "application/json" }
      })
      const opts = await res.json()

      if (opts.error || !Array.isArray(opts) || opts.length === 0) {
        dd.innerHTML = `<div class="px-4 py-3 text-xs text-red-400">Нет доступных меток</div>`
        return
      }

      dd.innerHTML = opts.map(o => `
        <button class="w-full text-left px-4 py-2.5 text-xs text-white/80 hover:bg-white/10
                       hover:text-white transition-colors flex items-center gap-2 whitespace-nowrap"
                data-mark="${o.mark}"
                data-action="click->security-mark#selectMark">
          <span class="font-medium text-white flex-1">${o.name}</span>
          <span class="text-white/40">SL: <strong class="text-white">${o.sl}</strong></span>
          <span class="font-bold text-amber-300 ml-1">${o.legal_abbr}</span>
        </button>
      `).join("")

      dd.dataset.loaded = "1"
    } catch {
      dd.innerHTML = `<div class="px-4 py-3 text-xs text-red-400">Ошибка загрузки списка</div>`
    }
  }

  async selectMark(event) {
    const mark = event.currentTarget.dataset.mark
    const csrf = document.querySelector('meta[name="csrf-token"]')?.content

    this._closeDropdown()

    // Сворачиваем пилюлю → запускаем снова с новой меткой
    animate(this.pillTarget, {
      opacity: [1, 0.5],
      maxWidth: [this.pillTarget.offsetWidth, 44],
      duration: 350,
      easing: "easeInQuad",
      onComplete: async () => {
        this.pillTarget.style.borderColor = ""
        this.pillTarget.style.opacity = "1"
        this.textTarget.style.display = ""
        this.textTarget.style.opacity = "0"
        this.textTarget.style.transform = "translateX(-8px)"
        this.errorMsgTarget.style.display = "none"

        animate(this.spinnerTarget, { opacity: [0, 1], duration: 200 })
        this._startSpinner()

        try {
          const res = await fetch("/p220/oracle_security_mark/select", {
            method: "PATCH",
            headers: {
              "Content-Type": "application/json",
              "X-CSRF-Token": csrf,
              "Accept": "application/json",
            },
            body: JSON.stringify({ mark }),
          })
          const data = await res.json()

          this._stopSpinner()

          if (data.status === "ok") {
            this._fillText(data)
            this._expandOk()
            // Перезагружаем страницу чтобы данные обновились с новой меткой
            setTimeout(() => Turbo.visit(window.location.href), 1200)
          } else {
            this._showError(data.error || "Не удалось сохранить метку")
            this._expandError()
          }
        } catch {
          this._stopSpinner()
          this._showError("Ошибка сохранения")
          this._expandError()
        }
      },
    })
  }
}
